import {
  Home,
  Mail,
  BarChart3,
  Settings,
  History,
  Server,
  Shield,
  Trello,
  Type,
  Inbox,
  User,
  Users,
  Bot,
  BookOpen,
  SlidersHorizontal,
  Sparkles,
  Brain,
  Database,
  Settings2,
  FileText,
  Zap,
  Plug,
  Wrench,
} from "lucide-react";
import type { NavSection } from "@skypointcloud/skypoint-ui";

const HomeIcon = Home;
const EnvelopesIcon = Mail;
const InsightsIcon = BarChart3;
const SettingsIcon = Settings;
const LogsIcon = History;
const InstancesIcon = Server;
const RolesIcon = Shield;
const PortalsIcon = Trello;
const FieldsIcon = Type;
const InboxIcon = Inbox;
const ContactsIcon = User;
const OrganizationsIcon = Users;
const AiPlaygroundIcon = Bot;
const AppPreferencesIcon = SlidersHorizontal;
const SkyAgentIcon = Sparkles;
const SkyBrainIcon = Brain;
const SkyBrainIndexIcon = Database;
const SkyBrainSettingsIcon = Settings2;
const SkyAgentConfigIcon = Wrench;

export const NAV_IDS = {
  DASHBOARD: "dashboard",
  SETTINGS: "settings",
  GENERAL: "general",
  USERS: "users",
  ROLES: "roles",
  LOGS: "logs",
  PLATFORM: "platform",
  INSTANCES: "instances",
  TENANTS: "tenants",
  SENIOR_LIVING: "senior-living",
  SENIOR_LIVING_RESIDENTS: "senior-living-residents",
  SENIOR_LIVING_PACKETS: "senior-living-packets",
  SENIOR_LIVING_DASHBOARD: "senior-living-dashboard",
  SENIOR_LIVING_COMMUNITY: "senior-living-community",
  HEALTH_PLAN: "health-plan",
  HEALTH_PLAN_PARTICIPANTS: "health-plan-participants",
  HEALTH_PLAN_REPRESENTATIVES: "health-plan-representatives",
  HEALTH_PLAN_ENROLLMENT: "health-plan-enrollment",
  NAVIGATOR: "navigator",
  NAVIGATOR_AGREEMENTS: "navigator-agreements",
  NAVIGATOR_ALERTS: "navigator-alerts",
  WEB_FORMS: "web-forms",
  AUTHORIZED_SIGNER_PORTAL: "authorized-signer-portal",
  TICKETS: "tickets",
  PORTALS: "portals",
  PORTALS_YOUR_WORK: "portals-your-work",
  PORTALS_DASHBOARD: "portals-dashboard",
  EMAIL_SETTINGS: "email-settings",
  APP_PREFERENCES: "app-preferences",
  FIELDS: "fields",
  CUSTOMER_PORTALS: "customer-portals",
  CUSTOMER_PORTAL_SETTINGS: "customer-portal-settings",
  USER_MANUAL: "user-manual",
  FOR_YOU: "for-you",
  PEOPLE_CONTACTS: "people-contacts",
  PEOPLE_ORGANIZATIONS: "people-organizations",
  AI_PLAYGROUND: "ai-playground",
  SKYAGENT: "skyagent",
  SKYAGENT_CONFIGURATION: "skyagent-configuration",
  SKYBRAIN: "skybrain",
  SKYBRAIN_SYSTEM_PROMPT: "skybrain-system-prompt",
  SKYBRAIN_MEMORY: "skybrain-memory",
  SKYBRAIN_SKILLS: "skybrain-skills",
  SKYBRAIN_INTEGRATIONS: "skybrain-integrations",
  SKYBRAIN_INDEX: "skybrain-index",
  SKYBRAIN_SETTINGS: "skybrain-settings",
} as const;

export type NavId = (typeof NAV_IDS)[keyof typeof NAV_IDS];

interface PermissionFlags {
  isFullAccess: boolean;
  isTenantAdmin: boolean;
  isPlatformAdmin: boolean;
  isInstanceAdmin: boolean;
  rbacIsAdmin?: boolean;
  rbacNavigation?: Record<string, boolean>;
  portals?: { key: string; name: string }[];
}

export function buildNavSections(permissions: PermissionFlags): NavSection[] {
  const { isTenantAdmin, isPlatformAdmin, rbacIsAdmin, rbacNavigation } = permissions;
  const isAdmin = isTenantAdmin || isPlatformAdmin || rbacIsAdmin === true;

  const shouldShow = (navKey: string): boolean => {
    if (isAdmin) return true;
    if (rbacNavigation) {
      return rbacNavigation[navKey] === true;
    }
    return false;
  };

  const sections: NavSection[] = [];

  // Home — always visible, no RBAC gate
  sections.push({
    header: "",
    items: [{ id: NAV_IDS.FOR_YOU, label: "Home", icon: HomeIcon }],
  });

  // ============================================================
  // SUPPORT TICKETS
  // ============================================================
  if (shouldShow("showSupportTickets")) {
    sections.push({
      header: "Support",
      items: [{ id: NAV_IDS.TICKETS, label: "Inbox", icon: InboxIcon }],
    });
  }

  // ============================================================
  // PORTALS - Project Management (Jira parity)
  // ============================================================
  if (isAdmin || shouldShow("showPortals")) {
    // Build portal children for flyout/accordion
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const portalChildren: any[] = [
      ...(permissions.portals ?? []).map((p) => ({
        id: `portal-${p.key}`,
        label: p.name,
      })),
      { id: "portal-all", label: "View All Portals" },
      { id: "portal-create", label: "Create a Portal" },
    ];

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const portalItems: any[] = [
      {
        id: NAV_IDS.PORTALS,
        label: "Portals",
        icon: PortalsIcon,
        children: portalChildren.length > 1 ? portalChildren : undefined,
      },
    ];

    if (isAdmin) {
      portalItems.push({
        id: NAV_IDS.PORTALS_DASHBOARD,
        label: "Dashboard",
        icon: InsightsIcon,
      });
    }

    sections.push({
      header: "Portals",
      items: portalItems,
    });
  }

  // ============================================================
  // AI PLAYGROUND — AI agent management
  // ============================================================
  if (isAdmin) {
    sections.push({
      header: "AI",
      items: [{ id: NAV_IDS.AI_PLAYGROUND, label: "AI Studio", icon: AiPlaygroundIcon }],
    });
  }

  // ============================================================
  // skyAgent + skyBrain
  // Two headers rather than one, matching skyCommand — the agent and the
  // corpus it reads are separate things to reason about, and merging them
  // hides which one is at fault when an answer is wrong.
  // ============================================================
  sections.push({
    header: "skyAgent",
    items: [
      { id: NAV_IDS.SKYAGENT, label: "Workspace", icon: SkyAgentIcon },
      // skyAgent's own instance-wide settings: its tenant instructions and the
      // kill switch every turn is gated on. Distinct from skyBrain's System
      // Prompt below — different rows in different tables.
      { id: NAV_IDS.SKYAGENT_CONFIGURATION, label: "Configuration", icon: SkyAgentConfigIcon },
    ],
  });
  // Order matches skyCommand's skyBrain section, with skySupport's own Index
  // and Settings at the end. Overview is Memory's first tab, not an entry.
  sections.push({
    header: "skyBrain",
    items: [
      { id: NAV_IDS.SKYBRAIN_SYSTEM_PROMPT, label: "Instructions", icon: FileText },
      { id: NAV_IDS.SKYBRAIN_MEMORY, label: "Memory", icon: SkyBrainIcon },
      { id: NAV_IDS.SKYBRAIN_SKILLS, label: "Skills", icon: Zap },
      { id: NAV_IDS.SKYBRAIN_INTEGRATIONS, label: "Integrations", icon: Plug },
      { id: NAV_IDS.SKYBRAIN_INDEX, label: "Index", icon: SkyBrainIndexIcon },
      { id: NAV_IDS.SKYBRAIN_SETTINGS, label: "Settings", icon: SkyBrainSettingsIcon },
    ],
  });

  // ============================================================
  // PEOPLE - Contacts & Organizations (always visible)
  // ============================================================
  sections.push({
    header: "People",
    items: [
      { id: NAV_IDS.PEOPLE_CONTACTS, label: "Contacts", icon: ContactsIcon },
      { id: NAV_IDS.PEOPLE_ORGANIZATIONS, label: "Organizations", icon: OrganizationsIcon },
    ],
  });

  // ============================================================
  // SETTINGS - Configuration, users, integrations
  // ============================================================

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const settingsItems: any[] = [];

  if (shouldShow("showSettings")) {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const settingsChildren: any[] = [
      { id: NAV_IDS.EMAIL_SETTINGS, label: "Email", icon: EnvelopesIcon },
      { id: NAV_IDS.FIELDS, label: "Fields", icon: FieldsIcon },
      { id: NAV_IDS.APP_PREFERENCES, label: "App Preferences", icon: AppPreferencesIcon },
    ];

    if (shouldShow("showUsers") || isAdmin) {
      settingsChildren.push({ id: NAV_IDS.ROLES, label: "Users & access", icon: RolesIcon });
    }
    if (isAdmin) {
      // settingsChildren.push({ id: NAV_IDS.INSTANCES, label: "Instances", icon: InstancesIcon });
      settingsChildren.push({
        id: NAV_IDS.CUSTOMER_PORTAL_SETTINGS,
        label: "Customer Portal",
        icon: PortalsIcon,
      });
      settingsChildren.push({
        id: NAV_IDS.USER_MANUAL,
        label: "Documentation",
        icon: BookOpen,
      });
    }

    settingsItems.push({
      id: NAV_IDS.SETTINGS,
      label: "Settings",
      icon: SettingsIcon,
      children: settingsChildren,
    });
  }

  sections.push({
    header: "Settings",
    items: settingsItems,
  });

  return sections;
}
